import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Stack,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import DrawerHeader from "./DrawerHeader";
import { saveAs } from "file-saver";

interface ImageViewerProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  title: string;
  error?: string;
  imageBlob?: Blob;
}

export const ImageViewer: React.FC<ImageViewerProps> = ({
  open,
  loading,
  onClose,
  title,
  imageBlob,
  error,
}) => {
  const { t } = useTranslation();

  const theme = useTheme();
  const anchor = theme.direction === "rtl" ? "left" : "right";

  const imageUrl = React.useMemo(() => {
    if (!imageBlob) return "";
    return URL.createObjectURL(imageBlob);
  }, [imageBlob]);

  const handleDownload = () => {
    if (!imageBlob) return;
    saveAs(imageBlob, title);
  };

  React.useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: "80%",
            maxWidth: 900,
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <DrawerHeader
        title={title}
        isLoading={loading && !error}
        onClose={onClose}
      />

      <Divider />

      {error && (
        <Alert severity="error" sx={{ my: 3, mx: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          minHeight: 400,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          backgroundColor: "#f7f7f7",
        }}
      >
        {imageBlob && imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={title}
            sx={{
              maxWidth: "100%",
              height: "auto",
              backgroundColor: "#fff",
            }}
          />
        ) : null}

        {(loading || !imageBlob) && !error && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      {imageBlob && !loading && (
        <>
          <Divider />
          <Stack sx={{ p: 1 }} spacing={1} direction="row" alignItems="center">
            <Button
              onClick={handleDownload}
              color="primary"
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              {t("imageViewer.download")}
            </Button>
          </Stack>
        </>
      )}
    </Drawer>
  );
};
